import React from 'react';
import { LayoutDashboard, Upload, Calendar, Clock, Zap, LogOut, Music2, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'upload', label: 'Upload Video', icon: Upload },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'queue', label: 'Queue', icon: Clock },
  { id: 'analytics', label: 'Analytics', icon: TrendingUp },
  { id: 'tiktok', label: 'TikTok Accounts', icon: Music2 },
];

export default function Sidebar({ active, onNavigate }) {
  const { user, logout } = useAuth();

  return (
    <aside style={styles.sidebar}>
      <div style={styles.logo}>
        <div style={styles.logoIcon}>
          <Zap size={20} color="#fff" fill="#fff" />
        </div>
        <span style={styles.logoText}>VidFlow</span>
      </div>

      <nav style={styles.nav}>
        <p style={styles.navSection}>MENU</p>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              style={{ ...styles.navItem, ...(isActive ? styles.navItemActive : {}) }}
              onClick={() => onNavigate(item.id)}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <Icon size={18} color={isActive ? '#fff' : 'var(--text-secondary)'} />
              <span style={{ ...styles.navLabel, color: isActive ? '#fff' : 'var(--text-secondary)' }}>
                {item.label}
              </span>
              {item.badge && <span style={styles.badge}>{item.badge}</span>}
              {isActive && <div style={styles.activeIndicator} />}
            </button>
          );
        })}
      </nav>

      <div style={styles.bottom}>
        <div style={styles.userCard}>
          <div style={styles.userAvatar}>
            {user?.name?.[0]?.toUpperCase() || 'U'}
          </div>
          <div style={styles.userInfo}>
            <p style={styles.userName}>{user?.name || 'User'}</p>
            <p style={styles.userEmail}>{user?.email || ''}</p>
          </div>
        </div>
        <button
          style={styles.logoutBtn}
          onClick={logout}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--pink-light)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <LogOut size={16} color="var(--pink)" />
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--pink)' }}>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}

const styles = {
  sidebar: {
    width: 240, minHeight: '100vh', background: 'var(--bg-secondary)',
    borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0,
  },
  logo: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '24px 20px 20px', borderBottom: '1px solid var(--border)',
  },
  logoIcon: {
    width: 34, height: 34, borderRadius: 10,
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 0 20px var(--accent-glow)',
  },
  logoText: { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.3px' },
  nav: { flex: 1, padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 2 },
  navSection: { fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '1.2px', padding: '4px 8px 8px' },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10,
    border: 'none', background: 'transparent', cursor: 'pointer', width: '100%',
    textAlign: 'left', position: 'relative', transition: 'background 0.15s',
  },
  navItemActive: { background: 'var(--accent-light)' },
  navLabel: { fontSize: 14, fontWeight: 500, flex: 1 },
  badge: { fontSize: 11, fontWeight: 700, background: 'var(--accent)', color: '#fff', borderRadius: 20, padding: '1px 7px' },
  activeIndicator: {
    position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
    width: 3, height: 20, background: 'var(--accent)', borderRadius: '3px 0 0 3px',
  },
  bottom: { padding: '12px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 },
  userCard: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px', borderRadius: 10,
    background: 'var(--bg-card)', border: '1px solid var(--border)',
  },
  userAvatar: {
    width: 32, height: 32, borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
  },
  userInfo: { flex: 1, minWidth: 0 },
  userName: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },
  userEmail: { fontSize: 10, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  logoutBtn: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 10,
    border: 'none', background: 'transparent', cursor: 'pointer', width: '100%', transition: 'background 0.15s',
  },
};
